import React, { useRef, useState } from "react";
import { Modal, message } from "antd";
import { Eye, Upload, X } from "lucide-react";

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const isImageFile = (fileName = "", fileType = "") => {
  const lowerName = fileName ? fileName.toLowerCase() : "";
  const lowerType = fileType ? fileType.toLowerCase() : "";
  return (
    lowerName.endsWith(".jpg") ||
    lowerName.endsWith(".jpeg") ||
    lowerName.endsWith(".png") ||
    lowerName.endsWith(".webp") ||
    lowerName.endsWith(".gif") ||
    lowerType.includes("image") ||
    lowerType === "jpg" ||
    lowerType === "jpeg" ||
    lowerType === "png"
  );
};

const getImageUrl = (name) => {
  if (!name) return "";
  if (name.startsWith("http://") || name.startsWith("https://")) {
    return name;
  }
  const baseUrl = process.env.REACT_APP_IMAGE_URL || "";
  return baseUrl.endsWith("/") ? `${baseUrl}${name}` : `${baseUrl}/${name}`;
};

/**
 * UploadPrescription
 *
 * Props:
 *  - uploadPrescription      : string | null  — pure base64 string of the uploaded PDF
 *  - uploadPrescriptionType  : string | null  — file type (e.g. "pdf")
 *  - uploadPrescriptionName  : string | null  — file name from API (stored in S3)
 *  - onChange(base64, type)  : called when file is picked
 *  - onClear()               : called when file is removed
 */
function UploadPrescription({
  uploadPrescription,
  uploadPrescriptionType,
  uploadPrescriptionName,
  onChange,
  onClear,
}) {
  const fileInputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type (PDF)
    if (file.type !== "application/pdf") {
      message.error("Only PDF files are allowed.");
      e.target.value = "";
      return;
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error(`File size must be less than ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const fullDataUrl = evt.target.result || "";
      const base64Pure = fullDataUrl.includes(",")
        ? fullDataUrl.split(",")[1]
        : fullDataUrl;
      const fileType =
        file.name?.split(".").pop()?.toLowerCase() ||
        (file.type?.includes("/") ? file.type.split("/")[1] : "pdf");

      onChange(base64Pure, fileType);
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange(null, null);
    }
  };

  const hasFile = Boolean(uploadPrescription || uploadPrescriptionName);

  // File label to show in pill
  const displayName = uploadPrescriptionName || "prescription.pdf";

  // Existing file URL from S3 (when available from API)
  const existingUrl = uploadPrescriptionName
    ? getImageUrl(uploadPrescriptionName)
    : null;

  // Newly uploaded file base64 preview URL
  const localPreviewUrl = uploadPrescription
    ? uploadPrescription.startsWith("data:")
      ? uploadPrescription
      : `data:application/pdf;base64,${uploadPrescription}`
    : null;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Upload button when no file exists */}
        {!hasFile ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-white flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#00A896] text-[#00A896] text-xs font-semibold hover:bg-[#E6F8F6] transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              Upload Prescription
            </button>
          </>
        ) : (
          <div className="flex items-center gap-2">
            {/* File name pill */}
            <span
              className="text-xs text-[#334155] bg-[#E6F8F6] border border-[#00A896] rounded-full px-2.5 py-0.5 font-medium max-w-[140px] truncate"
              title={displayName}
            >
              {displayName}
            </span>

            {/* Eye – preview */}
            <button
              onClick={() => setPreviewOpen(true)}
              title="Preview Prescription"
              className="w-7 h-7 bg-white flex items-center justify-center rounded-full border border-[#00A896] text-[#00A896] hover:bg-[#E6F8F6] transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>

            {/* X – remove */}
            <button
              onClick={handleClear}
              title="Remove file"
              className="w-7 h-7 bg-white flex items-center justify-center rounded-full border border-red-300 text-red-400 hover:bg-red-50 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Prescription Preview Modal */}
      <Modal
        title="Prescription Preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="75%"
        centered
        destroyOnClose
      >
        {/* If user uploaded a new file in this session, show local base64 preview */}
        {localPreviewUrl ? (
          <iframe
            src={localPreviewUrl}
            title="Prescription PDF"
            className="w-full rounded"
            style={{ height: "75vh", border: "none" }}
          />
        ) : existingUrl ? (
          /* If loading existing file from S3 via REACT_APP_IMAGE_URL */
          isImageFile(uploadPrescriptionName, uploadPrescriptionType) ? (
            <div className="flex justify-center items-center max-h-[75vh] overflow-auto">
              <img
                src={existingUrl}
                alt="Prescription"
                className="max-h-[75vh] max-w-full object-contain rounded"
              />
            </div>
          ) : (
            <iframe
              src={existingUrl}
              title="Prescription Document"
              className="w-full rounded"
              style={{ height: "75vh", border: "none" }}
            />
          )
        ) : null}
      </Modal>
    </>
  );
}

export default UploadPrescription;
